import dayjs from 'dayjs';
import { cloneDeep } from 'lodash';
import { mapGetters } from 'vuex';

import {
  cancelOrder,
  deleteScheduleVer2,
  redirectToReimburseSite,
  stopSchedule,
} from '@/api/orders';
import CancelConfirmation from '@/components/Order/OrderEdit/CancelConfirmation/CancelConfirmation.vue';
import CopyLinkModal from '@/components/Order/CopyLinkModal/CopyLinkModal.vue';
import DisableCustomLinkModal from '@/components/Order/DisableCustomLinkModal/DisableCustomLinkModal.vue';
import DraftDeleteConfirmation from '@/components/Order/OrderEdit/DraftDeleteConfirmation/DraftDeleteConfirmation.vue';
import EnableCustomLinkModal from '@/components/Order/EnableCustomLinkModal/EnableCustomLinkModal.vue';
import ReceiveNotificationModal from '@/components/Order/ReceiveNotificationModal/ReceiveNotificationModal.vue';
import ScheduleListModal from '@/components/Order/ScheduleListModal/ScheduleListModal.vue';
import ScheduleActionModal from '@/components/Order/ScheduleList/ScheduleActionModal/ScheduleActionModal.vue';
import {
  DEFAULT_PER_PAGE,
  FIRST_PAGE,
  FRONT_STAGE_URL,
  ORDER_STATUS_WITH_TYPE_TEXT,
  SNACKBAR_TYPES,
} from '@/utils/constants';
import bus from '@/utils/bus';

const ACTION_TYPE = {
  STOP: 'stop',
  DELETE: 'delete',
};

export default {
  name: 'ScheduleList',
  components: {
    CancelConfirmation,
    CopyLinkModal,
    DisableCustomLinkModal,
    DraftDeleteConfirmation,
    EnableCustomLinkModal,
    ReceiveNotificationModal,
    ScheduleListModal,
    ScheduleActionModal,
  },
  props: {
    currentPage: {
      default: FIRST_PAGE,
      type: Number,
    },
    currentPerPage: {
      default: DEFAULT_PER_PAGE,
      type: Number,
    },
    currentTab: {
      default: ORDER_STATUS_WITH_TYPE_TEXT.ONGOING,
      type: String,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    schedules: {
      required: true,
      type: Array,
    },
    sortField: {
      default: 'start',
      type: String,
    },
    sortOrder: {
      default: 'desc',
      type: String,
      validator: (val) => ['asc', 'desc'].includes(val),
    },
  },
  data() {
    return {
      action: null,
      actionOrder: {},
      isCancelingOrder: false,
      isModalLoading: false,
      openActionModal: false,
      openCancelConfirmation: false,
      openCopyLinkModal: false,
      openDisableCustomLinkModal: false,
      openEnableCustomLinkModal: false,
      openNotifyModal: false,
      openScheduleListModal: false,
      selectScheduleId: null,
    };
  },
  computed: {
    ...mapGetters(['isProduction']),
    actionSetting() {
      const setting = {
        [ACTION_TYPE.DELETE]: {
          title: '刪除例行性訂購排程',
          description: '此操作僅刪除本例行性訂購排程，已公告之訂購將不受影響。請確認是否刪除？',
          confirmText: '確認刪除',
          confirmLoading: '刪除中⋯',
        },
        [ACTION_TYPE.STOP]: {
          title: '終止例行性訂購排程',
          description: '此操作僅終止本例行性訂購排程中尚未公告之訂購。已公告之訂購將不受影響。請確認是否終止？',
          confirmText: '確認終止',
          confirmLoading: '終止中⋯',
        },
      };
      return setting[this.action] || {};
    },
  },
  methods: {
    dayjs,
    async deleteSchedule() {
      this.isModalLoading = true;
      try {
        await deleteScheduleVer2(this.selectScheduleId);
        this.selectScheduleId = null;
        this.$snackbar({
          type: SNACKBAR_TYPES.SUCCESS,
          title: '例行性訂購排程已刪除',
        });
        bus.$emit('GET_SCHEDULES');
        this.openActionModal = false;
      } catch (error) {
        const { message: msg = '' } = error;
        this.$snackbar({
          type: SNACKBAR_TYPES.ERROR,
          title: '刪除例行性訂購排程時發生錯誤，請重試一次',
          msg,
        });
      } finally {
        this.isModalLoading = false;
      }
    },
    async stopSchedule() {
      this.isModalLoading = true;
      try {
        await stopSchedule(this.selectScheduleId);
        this.selectScheduleId = null;
        this.$snackbar({
          type: SNACKBAR_TYPES.SUCCESS,
          title: '例行性訂購排程已終止',
        });
        bus.$emit('GET_SCHEDULES');
        this.openActionModal = false;
      } catch (error) {
        const { message: msg = '' } = error;
        this.$snackbar({
          type: SNACKBAR_TYPES.ERROR,
          title: '終止例行性訂購排程時發生錯誤，請重試一次',
          msg,
        });
      } finally {
        this.isModalLoading = false;
      }
    },
    onModalConfirm() {
      if (this.action === ACTION_TYPE.DELETE) {
        this.deleteSchedule();
      } else if (this.action === ACTION_TYPE.STOP) {
        this.stopSchedule();
      }
    },
    extraReceiveTimes(times = []) {
      const newArr = cloneDeep(times);
      newArr.shift();
      return newArr;
    },
    getLimitation(limit, limitAmount) {
      if (!limitAmount) {
        return '';
      }
      const count = this.$options.filters.commaFormat(limitAmount || 0);
      return limit === 2 ? `${count} 項` : `$${count}`;
    },
    getShareLink(order = {}) {
      const domain = this.isProduction ? FRONT_STAGE_URL.PRODUCTION : FRONT_STAGE_URL.STAGING;
      return `${domain}/menu/${order.id}?share=${order?.share?.share_key}`;
    },
    isShareModeOff(order = {}) {
      return !this.isShareModeOn && order?.report?.ordered_guest_count > 0;
    },
    isShareModeOn(order = {}) {
      return order?.share?.share_status;
    },
    async onDraftDelete(order = {}) {
      this.openConfirmDeleteDraftDialog = true;
      this.setActionOrder(order);
    },
    async onOrderCancel() {
      this.isCancelingOrder = true;
      try {
        await cancelOrder(this.actionOrder?.id);
        this.$snackbar({
          type: SNACKBAR_TYPES.SUCCESS,
          title: '團購已取消',
        });
        this.setActionOrder({});
        bus.$emit('GET_ORDERS');
        this.openCancelConfirmation = false;
      } catch (error) {
        const { message: msg = '' } = error;
        this.$snackbar({
          type: SNACKBAR_TYPES.ERROR,
          title: '取消團購時發生錯誤，請重試一次',
          msg,
        });
      } finally {
        this.isCancelingOrder = false;
      }
    },
    async onOpenUrl(order = {}) {
      try {
        const { data: { url = '/' } = {} } = await redirectToReimburseSite(order.id);
        window.open(url, '_blank');
      } catch (error) {
        const { message: msg = '' } = error;
        this.$snackbar({
          type: SNACKBAR_TYPES.ERROR,
          title: '開啟核銷網址時發生錯誤，請重試一次',
          msg,
        });
      }
    },
    getActivate(row) {
      const {
        activate = true,
        total_length: totalLength = 0,
        created_length: createdLength = 0,
      } = row || {};

      return activate && totalLength !== createdLength;
    },
    onSort(field, order) {
      this.$emit('sort', field, order);
    },
    onScheduleDelete(id) {
      this.action = ACTION_TYPE.DELETE;
      this.openActionModal = true;
      this.selectScheduleId = id;
    },
    onScheduleStop(id) {
      this.action = ACTION_TYPE.STOP;
      this.openActionModal = true;
      this.selectScheduleId = id;
    },
    setActionOrder(order = {}) {
      this.actionOrder = order || {};
    },
    toggleScheduleModel(show = false, order = {}) {
      this.setActionOrder({});
      this.openScheduleListModal = show;
      this.setActionOrder(order);
    },
    viewSchedule(id) {
      this.$router.push({
        path: `/order/schedule/view/${id}`,
      });
    },
    editSchedule(id) {
      this.$router.push({
        path: `/order/schedule/edit/${id}`,
      });
    },
    copySchedule(id) {
      this.$router.push({
        path: `/order/schedule/create/${id}`,
      });
    },
  },
};
